import {deserialize} from "serializr";

import firebase from "firebase";

import {WorkerKind} from "@/modules/redactor/models/worker/WorkerKind";
import {GlobalConfig} from "@/modules/redactor/models/global-config/GlobalConfig";
import {ProductGroupKind} from "@/modules/redactor/models/product/ProductGroupKind";

import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";

const REDACTOR_MODULE_NAMES = [
  "redactor-store-ad-placement",
  "redactor-store-ad-unit",

  "redactor-store-ad-energy-refill",
  "redactor-store-challenge",
  "redactor-store-smart-offer",
  "redactor-store-worker-team",
  "redactor-store-world-region",
  "redactor-store-world-region-territory",
  "redactor-store-swamp",
  "redactor-store-tutorial",
  "redactor-store-currency",
  "redactor-store-currency-kind",
  "redactor-store-currency-group",
  "redactor-store-discovery",
  "redactor-store-exchange",
  "redactor-store-prompt",
  "redactor-store-firefly",
  "redactor-store-firefly-v2",
  "redactor-store-setting",

  "redactor-store-product-item",
  "redactor-store-product-group",

  "redactor-store-reward",
  "redactor-store-recipe",
  "redactor-store-level",
  "redactor-store-map-group",
  "redactor-store-map-kind",
  "redactor-store-worker-kind",

  "redactor-store-talk",
  "redactor-store-quest",

  "redactor-store-card",
  "redactor-store-card-cluster",
  "redactor-store-card-package",

  "redactor-store-item",
  "redactor-store-item-kind",
  "redactor-store-item-group",
  "redactor-store-item-shop",
  "redactor-store-item-addon",
  "redactor-store-item-offer",
  "redactor-store-item-order",
  "redactor-store-item-recipe",
  "redactor-store-item-social",
  "redactor-store-item-cluster",

  "redactor-store-boat-order",
  "redactor-store-boat-config",

  "redactor-store-trader-order",
  "redactor-store-trader-offer",
  "redactor-store-trader-config",

  "redactor-store-daily-bonus-track",
  "redactor-store-daily-bonus-config",

  "redactor-store-daily-quest-order",
  "redactor-store-daily-quest-config",

  "redactor-store-league-config",
  "redactor-store-sky-sprint-config",
  "redactor-store-sky-common-config",

  "redactor-store-avatar",
  "redactor-store-booster",

  "redactor-store-map-addon",
  "redactor-store-map-addon-group",
];


export default {
    namespaced: true,
    name: "redactor-store",
    state: {
        mMapKind: null,
        mRootPath: null,
        mGlobalUID: null,
        mConfigUID: null,
        mSharedEditor: false,
        mLoadRevision: 0,

        mGlobalConfigs: [],
        mReadonly: false,
        isInitReady: false
    },
    getters: {
        getMapKind: (state) => () => {
            return state.mMapKind;
        },
        getRootPath: (state) => () => {
            return state.mRootPath;
        },
        getGlobalUID: (state) => () => {
            return state.mGlobalUID;
        },
        getConfigUID: (state) => () => {
            return state.mConfigUID;
        },
        getConfigPath: (state) => (global, subpath) => {
            let result = null;

            if (global === true) {
                result = state.mRootPath + state.mGlobalUID;
            } else if (global === false) {
                result = state.mRootPath + state.mConfigUID;
            } else {
                console.assert(false);
            }

            if (result && subpath) {
                result = result + subpath;
            }

            return result;
        },
        getGlobalConfig: (state) => () => {
            return state.mGlobalConfigs.find(v => v.uid === state.mGlobalUID);
        },
        getGlobalConfigs: (state) => () => {
            return state.mGlobalConfigs;
        },
        getReadonly: (state) => () => {
            return state.mReadonly;
        },
        getLoadRevision: (state) => () => {
            return state.mLoadRevision;
        },
    },
    mutations: {
        setMapKind(state, value) {
            state.mMapKind = value;
        },
        setRootPath(state, value) {
            state.mRootPath = value;
        },
        setGlobalUID(state, value) {
            console.log("global_uid: " + value);
            state.mGlobalUID = value;
        },
        setConfigUID(state, value) {
            console.log("config_uid: " + value);
            state.mConfigUID = value;
        },
        setGlobalConfigs(state, values) {
            state.mGlobalConfigs = values;
        },
        setReadonly(state, readonly) {
            state.mReadonly = readonly;
        },
        setSharedEditor(state, value) {
            state.mSharedEditor = Boolean(value);
        },
        bumpLoadRevision(state) {
            state.mLoadRevision += 1;
        },
        resetLoadRevision(state) {
            state.mLoadRevision = 0;
        },
        setInitReady(state, value) {
            state.isInitReady = value;
        }
    },
    actions: {
      init({ state, dispatch, commit, getters }, payload) {
        console.info("redactor-store initializing: " + payload.uid);

        const sameRoot = state.mRootPath === payload.path;
        const sameConfig = state.mConfigUID === payload.uid;
        const sameSharedMode = state.mSharedEditor === Boolean(payload.sharedMode);
        const alreadyInit = state.isInitReady && sameRoot && sameConfig && sameSharedMode;

        // if (alreadyInit) {
        //   console.info("redactor-store: same config & root, skip reload");
        //
        //   return Promise.resolve();
        // }

        commit("setRootPath", payload.path);
        commit("setMapKind", null);
        commit("setGlobalUID", null);
        commit("setConfigUID", payload.uid);
        commit("setReadonly", payload.readonly);
        commit("setSharedEditor", payload.sharedMode);

        const payloadForModule = {
          root_path: getters.getRootPath(),
          global_uid: getters.getGlobalUID(),
          config_uid: getters.getConfigUID(),
          readonly: getters.getReadonly()
        };

        if (!alreadyInit) {
          console.time("redactor-store/init-all-modules");

          REDACTOR_MODULE_NAMES.forEach(name => {
            const label = `sync init ${name}`;
            console.time(label);

            dispatch(name + "/init", payloadForModule, { root: true });

            console.timeEnd(label);
          });

          console.timeEnd("redactor-store/init-all-modules");
        }

        let globalConfigsPromise;

        if (state.mGlobalConfigs && state.mGlobalConfigs.length > 0) {
          globalConfigsPromise = Promise.resolve();
        } else {
          globalConfigsPromise = firebase
            .database()
            .ref("/release")
            .once("value")
            .then(snapshot => {
              const data = snapshot.val();

              if (!data) {
                return Promise.reject("Invalid loading global configs");
              }

              const configs = Object.values(data).map(v =>
                deserialize(GlobalConfig, v)
              );

              commit("setGlobalConfigs", configs);
            });
        }

        return globalConfigsPromise
          .then(() => dispatch("load"))
          .then(() => {
            if (!alreadyInit) {
              commit("setInitReady", true);
            }
          });
      },

      load({ dispatch, commit, getters }) {
        console.time("redactor-store/firebase-local");
        const path = getters.getConfigPath(false);
        console.log("firebase local path:", path);
        let sharedReloadPayload = null;
        let globalReloadPayload = null;
        let localReloadPayload = null;

        return firebase.database()
          .ref(path)
          .once("value")
          .then((snapshot) => {
            console.timeEnd("redactor-store/firebase-local");

            const data = snapshot.val();
            if (!data) {
              return Promise.reject("no config data");
            }

            const config = { ...data };
            return config;
          })
          .then((config) => {
            // Resolve shared config UID via Firebase (config-store is not registered in Redactor context)
            const configUID = getters.getConfigUID();
            const sharedConfigPromise = firebase.database()
              .ref("/release/" + configUID + "/sharedConfig")
              .once("value")
              .then((snap) => {
                if (snap.exists() && snap.val()) return snap.val();
                return firebase.database()
                  .ref("/configs/" + configUID + "/sharedConfig")
                  .once("value")
                  .then((snap2) => (snap2.exists() && snap2.val()) ? snap2.val() : null);
              })
              .catch(() => null);

            return sharedConfigPromise.then((sharedUID) => {
              // Load shared config data if present
              let loadSharedPromise = Promise.resolve();

              if (sharedUID) {
                console.time("redactor-store/firebase-shared");
                loadSharedPromise = firebase.database()
                  .ref("configs_data/" + sharedUID)
                  .once("value")
                  .then((snapshot) => {
                    console.timeEnd("redactor-store/firebase-shared");

                    const sharedData = snapshot.val();
                    if (!sharedData) {
                      console.warn("Shared config data not found for UID:", sharedUID);
                      return;
                    }

                    sharedReloadPayload = {
                      config: { ...sharedData },
                      local: config,
                      global: false,
                      shared: true
                    };

                    console.time("redactor-store/loadConfig-shared");
                    return dispatch("loadConfig", sharedReloadPayload).then(() => {
                      console.timeEnd("redactor-store/loadConfig-shared");
                    });
                  })
                  .catch((error) => {
                    console.warn("Failed to load shared config:", error);
                  });
              }

              return loadSharedPromise.then(() => {
                if (config["global_config"]) {
                  const global_uid = config["global_config"];
                  const configs = getters.getGlobalConfigs();

                  if (configs.find(v => v.uid === global_uid)) {
                    commit("setGlobalUID", global_uid);

                    REDACTOR_MODULE_NAMES.forEach(name => {
                      commit(name + "/setGlobalUID", global_uid, { root: true });
                    });
                  } else {
                    return Promise.reject(`Global config [${global_uid}] not found`);
                  }

                  console.time("redactor-store/firebase-global");
                  const globalPath = getters.getConfigPath(true);
                  console.log("firebase global path:", globalPath);

                  return firebase.database()
                    .ref(globalPath)
                    .once("value")
                    .then((snapshot) => {
                      console.timeEnd("redactor-store/firebase-global");

                      const data = snapshot.val();
                      if (!data) {
                        return Promise.reject("Failed to load global config");
                      }

                      globalReloadPayload = {
                        config: { ...data },
                        local: config,
                        global: true
                      };

                      console.time("redactor-store/loadConfig-global");
                      return dispatch("loadConfig", globalReloadPayload)
                        .then(() => {
                          console.timeEnd("redactor-store/loadConfig-global");

                          localReloadPayload = {
                            config,
                            local: config,
                            global: false
                          };

                          console.time("redactor-store/loadConfig-local");
                          return dispatch("loadConfig", localReloadPayload).then(() => {
                            console.timeEnd("redactor-store/loadConfig-local");
                          });
                        });
                    });

                } else {
                  commit("setGlobalUID", null);

                  REDACTOR_MODULE_NAMES.forEach(name => {
                    commit(name + "/setGlobalUID", null, { root: true });
                  });

                  localReloadPayload = {
                    config,
                    local: config,
                    global: false
                  };

                  console.time("redactor-store/loadConfig-local");
                  return dispatch("loadConfig", localReloadPayload).then(() => {
                    console.timeEnd("redactor-store/loadConfig-local");
                  });
                }
              });
            });
          })
          .then(() => {
            const payloads = [sharedReloadPayload, globalReloadPayload, localReloadPayload].filter(Boolean);

            return payloads.reduce((promise, payload) => {
              return promise.then(() => dispatch("loadDependentConfig", payload));
            }, Promise.resolve());
          })
          .then(() => {
            console.time("redactor-store/validate");
            return dispatch("validate").then(() => {
              console.timeEnd("redactor-store/validate");
              commit("bumpLoadRevision");
            });
          });
      },


      async loadConfig({ state, dispatch, commit }, payload) {
        let config = null;
        const shared = payload.shared || false;

        // This items should be load 1st

        // 1. map-group
        if ((config = payload.config["map-group"])) {
          await dispatch("redactor-store-map-group/load", {
            config,
            global: payload.global,
            shared
          }, { root: true });
        }

        // 2. map-kind
        let map = null;

        if ((config = payload.config["map-kind"])) {
          await dispatch("redactor-store-map-kind/load", {
            config,
            global: payload.global,
            shared
          }, { root: true });

          const rawCode = payload.local["map_id"];
          const code = Number(rawCode);
          const hasMapCode = Number.isFinite(code) && code > 0;
          const effectiveCode = hasMapCode ? code : (state.mSharedEditor ? null : 1);

          map = Number.isFinite(effectiveCode)
            ? RedactorUtils.getMapKindByCode(effectiveCode)
            : null;

          commit("setMapKind", map || null);

          if (map) {
            console.log("map_code", map.code);
          } else if (hasMapCode || !state.mSharedEditor) {
            console.warn("Map kind not found for code", effectiveCode);
          }
        }

        // 3. worker-kind
        if ((config = payload.config["worker-kind"])) {
          await dispatch("redactor-store-worker-kind/load", {
            config,
            global: payload.global,
            shared
          }, { root: true });

          WorkerKind.configure(map);
        }

        // 4. item-group
        if ((config = payload.config["item-group"])) {
          await dispatch("redactor-store-item-group/load", {
            config,
            global: payload.global,
            shared
          }, { root: true });
        }

        // 5. item-kind
        if ((config = payload.config["item-kind"])) {
          await dispatch("redactor-store-item-kind/load", {
            config,
            global: payload.global,
            shared
          }, { root: true });
        }

        // 6. currency-group
        if ((config = payload.config["currency-group"])) {
          await dispatch("redactor-store-currency-group/load", {
            config,
            global: payload.global,
            shared
          }, { root: true });
        }

        // 7. currency-kind
        if ((config = payload.config["currency-kind"])) {
          await dispatch("redactor-store-currency-kind/load", {
            config,
            global: payload.global,
            shared
          }, { root: true });
        }

        return dispatch("loadDependentConfig", payload);
      },

      loadDependentConfig({ dispatch }, payload) {
        if (!payload?.config) {
          return Promise.resolve();
        }

        let config = null;
        const shared = payload.shared || false;
        const loads = [];

        if ((config = payload.config["ad-placements"])) {
          loads.push(dispatch("redactor-store-ad-placement/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["ad-units"])) {
          loads.push(dispatch("redactor-store-ad-unit/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["currencies"])) {
          loads.push(dispatch("redactor-store-currency/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["exchange"])) {
          loads.push(dispatch("redactor-store-exchange/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["worker-teams"])) {
          loads.push(dispatch("redactor-store-worker-team/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["ad-settings"]?.["energy-refill"])) {
          loads.push(dispatch("redactor-store-ad-energy-refill/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["avatars"])) {
          loads.push(dispatch("redactor-store-avatar/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["map-addon-groups"])) {
          loads.push(dispatch("redactor-store-map-addon-group/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["map-addons"])) {
          loads.push(dispatch("redactor-store-map-addon/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["card-items"])) {
          loads.push(dispatch("redactor-store-card/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["card-cluster"])) {
          loads.push(dispatch("redactor-store-card-cluster/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["card-packages"])) {
          loads.push(dispatch("redactor-store-card-package/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["rewards"])) {
          loads.push(dispatch("redactor-store-reward/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["recipes"])) {
          loads.push(dispatch("redactor-store-recipe/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["settings"])) {
          loads.push(dispatch("redactor-store-setting/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["quests"])) {
          loads.push(dispatch("redactor-store-quest/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["orders"])) {
          loads.push(dispatch("redactor-store-boat-order/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["boat-configs"])) {
          loads.push(dispatch("redactor-store-boat-config/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["daily-quest-orders"])) {
          loads.push(dispatch("redactor-store-daily-quest-order/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["daily-quest-configs"])) {
          loads.push(dispatch("redactor-store-daily-quest-config/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["daily-bonus-tracks"])) {
          loads.push(dispatch("redactor-store-daily-bonus-track/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["daily-bonus-configs"])) {
          loads.push(dispatch("redactor-store-daily-bonus-config/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["world-regions"])) {
          loads.push(dispatch("redactor-store-world-region/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["world-region-territories"])) {
          loads.push(dispatch("redactor-store-world-region-territory/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["swamp"])) {
          loads.push(dispatch("redactor-store-swamp/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["trader-orders"])) {
          loads.push(dispatch("redactor-store-trader-order/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["trader-offers"])) {
          loads.push(dispatch("redactor-store-trader-offer/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["trader-configs"])) {
          loads.push(dispatch("redactor-store-trader-config/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["levels"])) {
          loads.push(dispatch("redactor-store-level/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["talks"])) {
          loads.push(dispatch("redactor-store-talk/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["discoveries"])) {
          loads.push(dispatch("redactor-store-discovery/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["product-items"])) {
          loads.push(dispatch("redactor-store-product-item/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["product-groups"])) {
          loads.push(dispatch("redactor-store-product-group/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["boosters"])) {
          loads.push(dispatch("redactor-store-booster/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["items-order"])) {
          loads.push(dispatch("redactor-store-item-order/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["smart-offers"])) {
          loads.push(dispatch("redactor-store-smart-offer/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["fireflies"])) {
          loads.push(dispatch("redactor-store-firefly/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["fireflies-v2"])) {
          loads.push(dispatch("redactor-store-firefly-v2/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["tutorials"])) {
          loads.push(dispatch("redactor-store-tutorial/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["items"])) {
          loads.push(dispatch("redactor-store-item/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["items-shop"])) {
          loads.push(dispatch("redactor-store-item-shop/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["items-addon"])) {
          loads.push(dispatch("redactor-store-item-addon/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["items-offer"])) {
          loads.push(dispatch("redactor-store-item-offer/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["items-recipe"])) {
          loads.push(dispatch("redactor-store-item-recipe/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["items-social"])) {
          loads.push(dispatch("redactor-store-item-social/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["items-cluster"])) {
          loads.push(dispatch("redactor-store-item-cluster/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["league-configs"])) {
          loads.push(dispatch("redactor-store-league-config/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["sky-sprint-configs"])) {
          loads.push(dispatch("redactor-store-sky-sprint-config/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["sky-common-configs"])) {
          loads.push(dispatch("redactor-store-sky-common-config/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["challenge-configs"])) {
          loads.push(dispatch("redactor-store-challenge/load", { config, global: payload.global, shared }, { root: true }));
        }

        if ((config = payload.config["prompts"])) {
          loads.push(dispatch("redactor-store-prompt/load", { config, global: payload.global, shared }, { root: true }));
        }

        return Promise.all(loads);
      },

      destroyed({ dispatch }) {
        console.time("redactor-store/destroyed");

        let promises = REDACTOR_MODULE_NAMES.map(name => {
          let result = dispatch(name + "/clear", {}, { root: true });

          if (!result) {
            result = Promise.reject("/clear undefined");
          }

          return result;
        });

        return Promise.all(promises).then(() => {
          console.timeEnd("redactor-store/destroyed");
        });
      },

        flush({dispatch}) {
            let promises = REDACTOR_MODULE_NAMES.map(name => {
                let result = dispatch(name + "/flush", {}, {root: true});

                if (!result) {
                    result = Promise.reject("/flush - undefined");
                }

                return result;
            });

            return Promise.all(promises)
        },

      clear({ dispatch, commit }) {
        console.time("redactor-store/clear-inner");

        const promises = REDACTOR_MODULE_NAMES.map(name => {
          const result = dispatch(name + "/clear", {}, { root: true });

          if (!result || typeof result.then !== "function") {
            return Promise.resolve();
          }

          return result;
        });

        return Promise.all(promises).then(() => {
          commit("setMapKind", null);
          commit("setSharedEditor", false);
          commit("resetLoadRevision");
          console.timeEnd("redactor-store/clear-inner");
        });
      },

        validate({dispatch}) {
            let promises = REDACTOR_MODULE_NAMES.map(name => {
                let result = dispatch(name + "/validate", {}, {root: true});

                if (!result) {
                    result = Promise.reject("/validate undefined");
                }

                return result;
            });
            return Promise.allSettled(promises).then(results => {
                let rejected = results.filter((v) => {
                    return v.status === 'rejected'
                });

                if (rejected.length > 0) {
                    let reasons = rejected.map(v => v.reason)
                    return Promise.reject(reasons.flat());
                } else {
                    return Promise.resolve();
                }
            });
        },

        getConfigData({/*commit*/}, uid) {
            const path = `configs_data/${uid}`;

            return firebase.database()
                .ref(path)
                .once("value")
                .then((snapshot) => snapshot.val());
        },

        buildPatch({dispatch, getters}) {
            let promises = REDACTOR_MODULE_NAMES.map(name => {
                let result = dispatch(name + "/buildPatch", {}, {root: true});

                if (!result) {
                    result = Promise.reject("/buildPatch - undefined");
                }

                return result;
            });

            let removeNull = function (obj) {
                Object.keys(obj).forEach(function (key) {
                    (obj[key] && typeof obj[key] === 'object') && removeNull(obj[key]) ||
                    (obj[key] === '' || obj[key] === null) && delete obj[key]
                });
                return obj;
            };

            return Promise.all(promises).then((values) => {
                let data = values.reduce((result, v) => {
                    return Object.assign(result, v);
                }, {});

                const mapKind = getters.getMapKind();
                if (mapKind && Number.isFinite(mapKind.code)) {
                    data["map_id"] = mapKind.code;
                }

                return Promise.resolve(removeNull(data));
            })
        },

        resetPatch({dispatch}) {
            let promises = REDACTOR_MODULE_NAMES.map(name => {
                let result = dispatch(name + "/resetPatch", {}, {root: true});

                if (!result) {
                    result = Promise.reject("/resetPatch - undefined");
                }

                return result;
            });

            return Promise.all(promises)
        },

        switchGlobalConfig({dispatch, getters}, value) {
            if (getters.getReadonly()) {
                return Promise.reject("The config is read-only");
            }

            return firebase.database()
                .ref(getters.getConfigPath(false, "/global_config"))
                .set(value)
                .then(() => {
                    return dispatch('load');
                });
        },

        patchProducts({getters}) {
            return firebase.database().ref(getters.getConfigPath(false))
                .once("value")
                .then((snapshot) => {
                    let promises = [];
                    let data = snapshot.val();

                    if (!data) {
                        return Promise.reject("no data");
                    }

                    let config = { ...data };

                    if ((data = config["products"])) {
                        var product_groups = {};
                        var product_items = {};

                        for (let key in data) {
                            let value = data[key];

                            if (!ProductGroupKind.getValue(value.kind)) {
                                continue;
                            }

                            let object = {};
                            object.uid = value.uid;
                            object.enabled = 1;
                            object.kind = value.kind;
                            object.order = value.order;

                            if ((config = value["config"])) {
                                object.reset_time = config["reset_time"];
                                object.reset_price = config["reset_price"];
                            }

                            product_groups[object.uid] = object;

                            let items = value["items"] || {};

                            for (let k in items) {
                                items[k].group = value.kind;
                                product_items[k] = items[k];
                            }
                        }

                        promises.push(firebase.database()
                            .ref(getters.getConfigPath(false, "/product-groups/"))
                            .set(product_groups, error => {
                                if (error) {
                                    console.error("push failed: " + error);
                                }
                            })
                        );

                        promises.push(firebase.database()
                            .ref(getters.getConfigPath(false, "/product-items/"))
                            .set(product_items, error => {
                                if (error) {
                                    console.error("push failed: " + error);
                                }
                            })
                        );

                        promises.push(firebase.database()
                            .ref(getters.getConfigPath(false, "/products/"))
                            .set(null, error => {
                                if (error) {
                                    console.error("push failed: " + error);
                                }
                            })
                        );
                    }

                    return Promise.all(promises);
                })
        }
    }
}
